import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { APP_NAME } from "@/lib/branding";
import { BrandLogo } from "@/components/BrandLogo";

const PLANS = [
  { code: "essential", label: "Essential", price: 29, tag: "TPE / Indépendants",
    features: ["Facturation Factur-X", "Dépenses", "Membres", "Journal d'audit"] },
  { code: "business", label: "Business", price: 79, tag: "PME en croissance", recommended: true,
    features: ["Facturation", "Achats + OCR", "Comptabilité", "Trésorerie", "Contrats", "Journal d'audit"] },
  { code: "scale", label: "Scale", price: 199, tag: "PME structurée",
    features: ["Tout Business", "RH & Paie", "Flotte", "CRM léger", `${APP_NAME} IA`] },
  { code: "enterprise", label: "Enterprise", price: 499, tag: "Groupe multi-entités",
    features: ["Tout Scale", "Multi-entités", "SSO SAML", "API publique", "SLA 99,95%"] },
  { code: "cabinet", label: "Cabinet", price: 349, tag: "Experts-comptables",
    features: ["Multi-dossiers illimité", "Portail client collaboratif", "Multi-pays FR/BE/LU", "API publique"] },
];

const STEPS = ["step1", "step2", "step3"];

export default function Register() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const initialPlan = (() => {
    const p = params.get("plan");
    return PLANS.find((x) => x.code === p) ? p : "business";
  })();
  const initialEmail = params.get("email") || "";
  const billing = params.get("billing") === "yearly" ? "yearly" : "monthly";
  const [form, setForm] = useState({
    full_name: "",
    email: initialEmail,
    password: "",
    company_name: "",
    siren: "",
    plan_code: initialPlan,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Si on arrive avec un plan payant en query, on reste sur le sélecteur en dernière étape.
    // Le badge visuel se met en évidence tout seul via form.plan_code.
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const next = () => {
    setError("");
    if (step === 0 && (!form.full_name || !form.email || form.password.length < 8)) {
      setError("Nom, email et un mot de passe (≥8 caractères) sont requis.");
      return;
    }
    if (step === 1 && !form.company_name) {
      setError("Le nom de l'entreprise est requis.");
      return;
    }
    setStep((s) => Math.min(s + 1, 2));
  };

  const submit = async () => {
    setError("");
    setLoading(true);
    const payload = { ...form };
    if (!payload.siren) delete payload.siren;
    const res = await register(payload);
    if (!res.ok) {
      setLoading(false);
      setError(res.error);
      return;
    }
    // Tous les plans sont payants → Stripe Checkout systématique après signup.
    if (form.plan_code) {
      try {
        const r = await api.post("/billing/checkout", {
          lookup_key: `${form.plan_code}_${billing}`,
          origin_url: window.location.origin,
        });
        if (r.data?.checkout_url) {
          window.location.assign(r.data.checkout_url);
          return;
        }
      } catch (e) {
        toast.error(
          "Compte créé. La redirection paiement a échoué : "
            + apiError(e)
            + ". Vous pouvez souscrire depuis Paramètres → Abonnement.",
        );
      }
    }
    setLoading(false);
    navigate("/app");
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6 antialiased"
      style={{ fontFamily: "'Plus Jakarta Sans', 'Manrope', system-ui, sans-serif" }}
    >
      <div className="w-full max-w-2xl">
        <div className="flex items-center justify-between mb-10">
          <Link to="/welcome" className="flex items-center gap-2.5 group">
            <BrandLogo variant="icon" className="h-8 w-8" />
            <span className="font-bold tracking-tight text-[15px] text-[#0B0F17]">
              {APP_NAME}
            </span>
          </Link>
          <Link
            to="/welcome"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#0B0F17] transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Retour au site
          </Link>
        </div>

        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0B0F17] leading-[1.1]">
          {t("auth.registerTitle")}
        </h1>
        <p className="mt-3 text-sm text-slate-600">{t("auth.registerSubtitle")}</p>

        {/* Stepper */}
        <div className="flex items-center gap-2 mt-8 mb-6" data-testid="register-stepper">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className={cn(
                "h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold border transition-colors",
                i < step && "bg-emerald-500 text-white border-emerald-500",
                i === step && "bg-[#0B0F17] text-white border-[#0B0F17]",
                i > step && "bg-white text-slate-400 border-slate-200"
              )}>
                {i < step ? <Check className="h-4 w-4" /> : i + 1}
              </div>
              {i < STEPS.length - 1 && (
                <div className={cn("w-14 h-px", i < step ? "bg-emerald-500" : "bg-slate-200")} />
              )}
            </div>
          ))}
        </div>

        <Card className="p-6 sm:p-8 bg-white border-slate-200 rounded-2xl shadow-sm">
          {error && (
            <Alert variant="destructive" className="mb-5" data-testid="register-error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {step === 0 && (
            <div className="space-y-4" data-testid="register-step-1">
              <div className="text-xs font-medium uppercase tracking-wider text-slate-500">
                {t("auth.step1")}
              </div>
              <div className="grid gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="full_name">{t("auth.fullName")}</Label>
                  <Input id="full_name" value={form.full_name} onChange={upd("full_name")}
                    data-testid="register-fullname-input" className="h-11" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reg_email">{t("common.email")}</Label>
                  <Input id="reg_email" type="email" value={form.email} onChange={upd("email")}
                    data-testid="register-email-input" className="h-11" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reg_pw">{t("common.password")}</Label>
                  <Input id="reg_pw" type="password" value={form.password} onChange={upd("password")}
                    data-testid="register-password-input" className="h-11" />
                  <p className="text-xs text-slate-400">Au moins 8 caractères.</p>
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="register-step-2">
              <div className="text-xs font-medium uppercase tracking-wider text-slate-500">
                {t("auth.step2")}
              </div>
              <div className="grid gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="company">{t("auth.companyName")}</Label>
                  <Input id="company" value={form.company_name} onChange={upd("company_name")}
                    data-testid="register-company-input" className="h-11" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="siren">{t("auth.siren")}</Label>
                  <Input id="siren" value={form.siren} onChange={upd("siren")}
                    data-testid="register-siren-input" className="h-11"
                    placeholder="9 chiffres" />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="register-step-3">
              <div className="text-xs font-medium uppercase tracking-wider text-slate-500">
                {t("auth.step3")}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {PLANS.map((p) => (
                  <button
                    type="button"
                    key={p.code}
                    onClick={() => setForm({ ...form, plan_code: p.code })}
                    data-testid={`register-plan-${p.code}`}
                    className={cn(
                      "text-left rounded-xl border p-4 transition-all",
                      form.plan_code === p.code
                        ? "border-[#0B0F17] ring-2 ring-[#0B0F17]/10 bg-slate-50/60"
                        : "border-slate-200 hover:border-slate-300 bg-white"
                    )}
                  >
                    <div className="flex items-baseline justify-between">
                      <div className="font-semibold text-[#0B0F17]">{p.label}</div>
                      {p.recommended && (
                        <span className="text-[10px] font-bold uppercase tracking-widest text-[#4F46E5]">
                          Recommandé
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{p.tag}</div>
                    <div className="mt-2 flex items-baseline">
                      <span className="text-2xl font-bold text-[#0B0F17] tabular-nums"
                            style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                        {p.price}
                      </span>
                      <span className="text-sm text-slate-500 font-normal ml-0.5">{t("plan.priceSuffix")}</span>
                    </div>
                    <ul className="mt-3 space-y-1.5">
                      {p.features.map((f) => (
                        <li key={f} className="text-xs text-slate-600 flex items-center gap-1.5">
                          <Check className="h-3 w-3 text-emerald-600" /> {f}
                        </li>
                      ))}
                    </ul>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-between items-center mt-8">
            {step > 0 ? (
              <Button variant="ghost" onClick={() => setStep(step - 1)}
                      data-testid="register-back-btn"
                      className="text-slate-600 hover:text-[#0B0F17]">
                <ArrowLeft className="h-4 w-4 mr-1" /> {t("common.back")}
              </Button>
            ) : <span />}
            {step < 2 ? (
              <Button onClick={next} data-testid="register-next-btn"
                      className="bg-[#0B0F17] hover:bg-[#1E293B] text-white shadow-lg shadow-slate-900/10">
                {t("common.continue")} <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={loading}
                      data-testid="register-submit-btn"
                      className="bg-[#0B0F17] hover:bg-[#1E293B] text-white shadow-lg shadow-slate-900/10">
                {loading ? t("common.loading") : t("auth.createAccount")}
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </Card>

        <div className="mt-6 text-sm text-slate-600 text-center">
          {t("auth.hasAccount")}{" "}
          <Link to="/login" data-testid="link-to-login" className="text-[#4F46E5] hover:text-[#4338CA] font-semibold">
            {t("common.signIn")}
          </Link>
        </div>
      </div>
    </div>
  );
}
